import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { PatchNoteBody } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

/** Updates the content of an existing note and invalidates relevant caches. */
export function usePatchNote() {
  const api = useOsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: PatchNoteBody }) => {
      const response = await api.patch('/notes/:id', { params: { id }, body })
      if (!response.success) throw new Error(response.error.message)
      return response.data.note
    },
    onSuccess: (note, { body }) => {
      queryClient.invalidateQueries({ queryKey: ['patient-timeline', note.patientId] })
      // Attachments can be added or removed via a patch, which surfaces in the patient files box —
      // only refresh it when this patch actually touched attachments.
      if ((body.addFileKeys?.length ?? 0) > 0 || (body.removeFileIds?.length ?? 0) > 0) {
        queryClient.invalidateQueries({ queryKey: ['patient-files', note.patientId] })
      }
      if (note.consultationId) {
        queryClient.invalidateQueries({ queryKey: ['consultation', note.consultationId] })
      }
    },
  })
}
